import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Switch, Text } from "@pentabd/ui";

import { yupResolver } from "@hookform/resolvers/yup";
import { useMetaData } from "@hooks/inventory/roomConfiguration/useMetaData";
import { useLanguage } from "@hooks/useLanguage";
import {
  ASSIGN_DESK_OR_BED,
  EmployeeAssignDeskOrBedFormDataType,
  employeeAssignDeskOrBedValidation,
} from "@validations/Inventory/employee/assignDeskOrBed";

import {
  ErrorModal,
  InputTextRow,
  SectionTitle,
  SelectControllerRow,
  SuccessModal,
} from "@components";
import { ROOM_META_DATA } from "@constants/inventory/room-meta-data";
import { getConvertedMetaDataForEmployee } from "@helpers/inventory/metaDataConvert";
import { useEmployeeGeneralInfo } from "@hooks/inventory/employee/useEmployeeGeneralInfo";
import useActionCreator from "@redux/hooks/useActionCreator";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import {
  BarrackInformationType,
  EmployeePropType,
  GeneralInfoResponseType,
  OfficeInformationType,
} from "@type/inventory/employee";

import { ROUTES } from "@constants/routes";

const {
  INVENTORY: { EMPLOYEES },
} = ROUTES;

export default function GeneralInfo({ id, isProfile }: EmployeePropType) {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isLivesOnPremises, setIsLivesOnPremises] = useState<boolean>(false);

  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { inventoryActionsHandlers } = useActionCreator();
  const { assignEmployeeDeskOrBedActionHandler } = inventoryActionsHandlers;
  const { employeesState } = useEmployeeGeneralInfo(id);
  const { data: office } = useMetaData(ROOM_META_DATA.OFFICE);
  const { data: building, setQuery: setBuildingQueries } = useMetaData(
    ROOM_META_DATA.BUILDING,
  );
  const { data: rooms, setQuery: setRoomQueries } = useMetaData(
    ROOM_META_DATA.ROOM,
  );
  const { data: desks } = useMetaData(ROOM_META_DATA.DESK);
  const { data: roomsType } = useMetaData(ROOM_META_DATA.ROOM_TYPE);

  const { generalInfo } = employeesState;
  const { data } = generalInfo || ({} as GeneralInfoResponseType);
  const { officeId, buildingId, roomId, roomTypeId, deskNumberId } =
    data?.officeInformation || ({} as OfficeInformationType);
  const {
    officeId: barrackOfficeId,
    buildingId: barrackBuildingId,
    roomId: barrackRoomId,
    barrackNumber,
    bedNumberId,
  } = data?.barrackInformation || ({} as BarrackInformationType);

  const {
    OFFICE,
    BUILDING,
    ROOM_TYPE,
    ROOM,
    DESK_NUMBER,

    LIVES_ON_PREMISES,

    LIVES_ON_PREMISES_OFFICE,
    LIVES_ON_PREMISES_BUILDING,
    LIVES_ON_PREMISES_ROOM,
    LIVES_ON_PREMISES_BARRACK_NUMBER,
    LIVES_ON_PREMISES_BED_NUMBER,
  } = ASSIGN_DESK_OR_BED;

  const {
    control,
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
    // watch,
  } = useForm<EmployeeAssignDeskOrBedFormDataType>({
    resolver: yupResolver(employeeAssignDeskOrBedValidation),
  });

  // const watchOffice = watch(OFFICE);
  // const watchBuilding = watch(BUILDING);
  // const watchRoomType = watch(ROOM_TYPE);

  const { createError, createSuccess, isCreateLoading } = useCustomSelector(
    (store) => store.inventory.employeesState,
  );

  const onSubmit: SubmitHandler<EmployeeAssignDeskOrBedFormDataType> = (
    data: any,
  ) => {
    const formattedData = {
      livesOnPremises: data.lives,
      officeInformation: {
        officeId: Number(data.officeId),
        buildingId: Number(data.buildingId),
        roomId: Number(data.roomId),
        roomTypeId: Number(data.roomTypeId),
        deskNumberId: Number(data.deskNumberId),
      },
      barrackInformation: {
        officeId: data.livesOfficeId ? Number(data.livesOfficeId) : null,
        buildingId: data.livesBuildingId ? Number(data.livesBuildingId) : null,
        roomId: data.livesRoomId ? Number(data.livesRoomId) : null,
        bedNumberId: data.livesBedNumberId
          ? Number(data.livesBedNumberId)
          : null,
        barrackNumberId: data.livesBarrackNumberId
          ? Number(data.livesBarrackNumberId)
          : null,
      },
    };

    assignEmployeeDeskOrBedActionHandler(id || "", formattedData);
  };

  const successAction = () => {
    reset();
    setIsSuccessModalOpen(false);
    navigate(EMPLOYEES);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  useEffect(() => {
    setIsLivesOnPremises(barrackOfficeId === null ? false : true);
  }, [data]);

  useEffect(() => {
    if (data?.officeInformation || data?.barrackInformation) {
      setValue(OFFICE, officeId ? `${officeId}` : "");
      setValue(BUILDING, buildingId ? `${buildingId}` : "");
      setValue(ROOM, roomId ? `${roomId}` : "");
      setValue(ROOM_TYPE, roomTypeId ? `${roomTypeId}` : "");
      setValue(DESK_NUMBER, deskNumberId ? `${deskNumberId}` : "");
      setValue(
        LIVES_ON_PREMISES_OFFICE,
        barrackOfficeId ? `${barrackOfficeId}` : "",
      );
      setValue(
        LIVES_ON_PREMISES_BUILDING,
        barrackBuildingId ? `${barrackBuildingId}` : "",
      );
      setValue(LIVES_ON_PREMISES_ROOM, barrackRoomId ? `${barrackRoomId}` : "");
      setValue(
        LIVES_ON_PREMISES_BARRACK_NUMBER,
        barrackNumber ? `${barrackNumber}` : "",
      );
      setValue(
        LIVES_ON_PREMISES_BED_NUMBER,
        bedNumberId ? `${bedNumberId}` : "",
      );
    }
  }, [data]);

  useEffect(() => {
    setIsSuccessModalOpen(createSuccess);
    setIsErrorModalOpen(createError);
  }, [createSuccess, createError]);

  // useEffect(() => {
  //   if (watchOffice) {
  //     setBuildingQueries({
  //       officeId: watchOffice,
  //     });
  //   }
  // }, [watchOffice, setBuildingQueries]);

  // useEffect(() => {
  //   if (watchBuilding && watchRoomType) {
  //     setRoomQueries({
  //       buildingId: watchBuilding,
  //       roomTypeId: watchRoomType,
  //     });
  //   }
  // }, [watchBuilding, watchRoomType, setRoomQueries]);

  // useEffect(() => {
  //   reset();
  // }, []);

  return (
    <>
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={successAction}
          subtitle={"You have assigned new desk/bed"}
        />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <SectionTitle label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.DESK" />
          <div className="d-flex flex-column gap-6">
            <SelectControllerRow
              label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.OFFICE"
              control={control}
              name={OFFICE}
              register={{ ...register(OFFICE) }}
              options={getConvertedMetaDataForEmployee(office, language)}
              setValue={setValue}
              error={errors}
              required
              // disabled={isProfile}
              disabled
            />
            <SelectControllerRow
              label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.BUILDING"
              control={control}
              name={BUILDING}
              register={{ ...register(BUILDING) }}
              options={getConvertedMetaDataForEmployee(building, language)}
              setValue={setValue}
              error={errors}
              required
              // disabled={isProfile}
              disabled
            />
            <SelectControllerRow
              label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.ROOM_TYPE"
              control={control}
              name={ROOM_TYPE}
              register={{ ...register(ROOM_TYPE) }}
              options={getConvertedMetaDataForEmployee(roomsType, language)}
              setValue={setValue}
              error={errors}
              required
              // disabled={isProfile}
              disabled
            />
            <SelectControllerRow
              label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.ROOM"
              control={control}
              name={ROOM}
              register={{ ...register(ROOM) }}
              options={getConvertedMetaDataForEmployee(rooms, language)}
              setValue={setValue}
              error={errors}
              required
              // disabled={isProfile}
              disabled
            />
            <SelectControllerRow
              label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.DESK_NUMBER"
              control={control}
              name={DESK_NUMBER}
              register={{ ...register(DESK_NUMBER) }}
              options={getConvertedMetaDataForEmployee(
                desks?.map((item: any) => ({
                  id: item.id,
                  deskNumber: item.deskNumber,
                })),
                language,
              )}
              setValue={setValue}
              error={errors}
              required
              // disabled={isProfile}
              disabled
            />
            {!isProfile && (
              <div className="row">
                <div className="col-lg-3">
                  <Text weight="semibold" size="sm" color="title">
                    {t(
                      "INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.LIVES_ON_PREMISES.LABEL",
                    )}
                  </Text>
                </div>
                <div className="col-lg-7 d-flex">
                  <Switch
                    id={LIVES_ON_PREMISES}
                    {...register(`${LIVES_ON_PREMISES}`)}
                    checked={isLivesOnPremises}
                    onChange={(e) => {
                      e.target.checked
                        ? setIsLivesOnPremises(true)
                        : setIsLivesOnPremises(false);

                      setValue(LIVES_ON_PREMISES, e.target.checked);
                    }}
                    size="sm"
                    controlling
                  />
                  <Text
                    weight="bold"
                    color="primary"
                    component="h4"
                    sizeType="display"
                    className="ms-5"
                  >
                    {isLivesOnPremises ? "Yes" : "No"}
                  </Text>
                </div>
              </div>
            )}

            {isLivesOnPremises && (
              <>
                <SectionTitle label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.BED" />
                <SelectControllerRow
                  label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.OFFICE"
                  control={control}
                  name={LIVES_ON_PREMISES_OFFICE}
                  register={{ ...register(LIVES_ON_PREMISES_OFFICE) }}
                  options={getConvertedMetaDataForEmployee(office, language)}
                  setValue={setValue}
                  error={errors}
                  required
                  // disabled={isProfile}
                  disabled
                />
                <SelectControllerRow
                  label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.BUILDING"
                  control={control}
                  name={LIVES_ON_PREMISES_BUILDING}
                  register={{ ...register(LIVES_ON_PREMISES_BUILDING) }}
                  options={getConvertedMetaDataForEmployee(building, language)}
                  setValue={setValue}
                  error={errors}
                  required
                  // disabled={isProfile}
                  disabled
                />
                <SelectControllerRow
                  label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.ROOM"
                  control={control}
                  name={LIVES_ON_PREMISES_ROOM}
                  register={{ ...register(LIVES_ON_PREMISES_ROOM) }}
                  options={getConvertedMetaDataForEmployee(rooms, language)}
                  setValue={setValue}
                  error={errors}
                  required
                  // disabled={isProfile}
                  disabled
                />
                <InputTextRow
                  label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.BARRACK_NUMBER"
                  register={{ ...register(LIVES_ON_PREMISES_BARRACK_NUMBER) }}
                  error={errors}
                  // disabled={isProfile}
                  disabled
                  type="number"
                />
                {/* <SelectControllerRow
                  label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.BED_NUMBER"
                  control={control}
                  name={LIVES_ON_PREMISES_BED_NUMBER}
                  register={{ ...register(LIVES_ON_PREMISES_BED_NUMBER) }}
                  options={getConvertedMetaDataForEmployee(
                    desks?.map((item: any) => ({
                      id: item.id,
                      deskNumber: item.deskNumber,
                    })),
                    language,
                  )}
                  setValue={setValue}
                  error={errors}
                  required
                  disabled={isProfile}
                /> */}
                <InputTextRow
                  label="INVENTORY_EMPLOYEE.EMPLOYEE_ASSIGN_DESK.FORM.FIELDS.BED_NUMBER"
                  register={{ ...register(LIVES_ON_PREMISES_BED_NUMBER) }}
                  error={errors}
                  // disabled={isProfile}
                  disabled
                  type="number"
                />
              </>
            )}

            {/* {!isProfile && (
              <div className="row mb-10">
                <div className="col-lg-10 text-end">
                  <SubmitButton label="save" loading={isCreateLoading} />
                </div>
              </div>
            )} */}
          </div>
        </div>
      </form>
    </>
  );
}
